<template>
  <v-container max-width="800" class="py-4">

    <!-- Mode selector -->
    <v-btn-toggle
      v-model="mode"
      mandatory
      rounded="lg"
      variant="outlined"
      color="primary"
      class="mb-4 d-flex"
    >
      <v-btn value="barcode" class="flex-grow-1">
        <v-icon start>mdi-barcode</v-icon>
        Code-barres
      </v-btn>
      <v-btn value="receipt" class="flex-grow-1">
        <v-icon start>mdi-receipt-text-outline</v-icon>
        Ticket de caisse
      </v-btn>
    </v-btn-toggle>

    <!-- Mode description -->
    <p class="text-caption text-medium-emphasis mb-4">
      <template v-if="mode === 'barcode'">
        Photographiez un code-barres EAN-13 pour identifier le produit.
      </template>
      <template v-else>
        Photographiez un ticket de caisse&nbsp;: les codes GTIN à 13&nbsp;chiffres seront extraits automatiquement.
      </template>
    </p>

    <!-- Image capture -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      capture="environment"
      class="d-none"
      @change="onFileChange"
    />

    <div
      v-if="!previewUrl"
      class="d-flex flex-column align-center justify-center rounded-lg bg-grey-lighten-4 mb-4"
      style="height: 220px; border: 2px dashed #bdbdbd; cursor: pointer"
      @click="fileInput?.click()"
    >
      <v-icon size="48" color="grey" class="mb-2">mdi-camera-plus-outline</v-icon>
      <span class="text-body-2 text-medium-emphasis">Prendre ou importer une photo</span>
    </div>

    <div v-else class="mb-4 position-relative">
      <v-img
        :src="previewUrl"
        max-height="320"
        cover
        rounded="lg"
        class="bg-grey-lighten-3"
      />
      <v-btn
        icon="mdi-close"
        size="small"
        variant="flat"
        color="white"
        style="position: absolute; top: 8px; right: 8px; opacity: 0.85"
        @click="reset"
      />
    </div>

    <!-- Actions -->
    <div class="d-flex ga-3 mb-4">
      <v-btn
        v-if="previewUrl"
        variant="tonal"
        prepend-icon="mdi-camera-plus-outline"
        class="flex-grow-1"
        @click="fileInput?.click()"
      >
        Changer
      </v-btn>

      <v-btn
        v-if="previewUrl && !scanning"
        color="primary"
        variant="elevated"
        prepend-icon="mdi-magnify-scan"
        class="flex-grow-1"
        @click="scan"
      >
        {{ mode === 'barcode' ? 'Détecter le code-barres' : 'Extraire les GTINs' }}
      </v-btn>

      <v-btn
        v-if="scanning"
        color="primary"
        variant="elevated"
        loading
        class="flex-grow-1"
        disabled
      >
        Analyse en cours…
      </v-btn>
    </div>

    <!-- Save purchase -->
    <div v-if="results.length > 0 && !lookingUp && !saved" class="mb-4">
      <v-btn
        color="success"
        variant="elevated"
        prepend-icon="mdi-content-save-outline"
        block
        :loading="saving"
        @click="savePurchase"
      >
        Enregistrer dans mes achats
      </v-btn>
    </div>

    <v-snackbar v-model="saveSnackbar" color="success" timeout="3000" location="top">
      <v-icon start>mdi-check-circle-outline</v-icon>
      Achat enregistré avec succès
    </v-snackbar>

    <!-- Tesseract progress (receipt mode only) -->
    <v-progress-linear
      v-if="scanning && mode === 'receipt'"
      :model-value="ocrProgress"
      color="primary"
      rounded
      height="6"
      class="mb-4"
    />

    <!-- Error -->
    <v-alert
      v-if="errorMsg"
      type="error"
      variant="tonal"
      closable
      class="mb-4"
      @click:close="errorMsg = ''"
    >
      {{ errorMsg }}
    </v-alert>

    <!-- API lookup progress -->
    <v-progress-linear
      v-if="lookingUp"
      indeterminate
      color="primary"
      rounded
      height="6"
      class="mb-4"
    />

    <!-- Results -->
    <template v-if="results.length > 0 && !lookingUp">
      <p class="text-body-2 font-weight-medium mb-3">
        {{ results.length }} GTIN{{ results.length > 1 ? 's' : '' }} analysé{{ results.length > 1 ? 's' : '' }}
      </p>
      <div class="d-flex flex-column ga-3">
        <template v-for="item in results" :key="item.gtin">
          <RecallCard
            v-for="recall in item.recalls"
            :key="recall.id"
            :recall="recall"
          />
          <v-card v-if="item.recalls.length === 0" rounded="lg">
            <div class="d-flex" style="height: 120px">
              <div
                class="d-flex align-center justify-center bg-grey-lighten-3 rounded-s-lg flex-shrink-0"
                style="width: 100px; height: 120px"
              >
                <v-icon size="40" color="grey">mdi-barcode-off</v-icon>
              </div>
              <div class="d-flex flex-column justify-center pa-3" style="min-width: 0">
                <span class="text-body-2 font-weight-medium">{{ item.gtin }}</span>
                <span class="text-caption text-medium-emphasis mt-1">Aucun rappel trouvé pour ce GTIN</span>
              </div>
            </div>
          </v-card>
        </template>
      </div>
    </template>

    <v-alert
      v-else-if="scanned && !scanning && !lookingUp && results.length === 0"
      type="warning"
      variant="tonal"
    >
      <template v-if="mode === 'barcode'">
        Aucun code-barres EAN-13 détecté. Réessayez avec une image plus nette ou passez en mode "Ticket de caisse".
      </template>
      <template v-else>
        Aucun GTIN valide trouvé dans l'image. Vérifiez que les chiffres sont lisibles.
      </template>
    </v-alert>

  </v-container>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import { createWorker, PSM } from 'tesseract.js'
import RecallCard, { type RecallItem } from './RecallCard.vue'

interface GtinResult {
  gtin: string
  recalls: RecallItem[]
}

interface HydraCollection {
  totalItems: number
  member: RecallItem[]
}

const SCAN_MAX_EDGES = [1280, 960, 640] as const

function isValidEAN13(code: string): boolean {
  if (!/^\d{13}$/.test(code)) return false
  const d   = code.split('').map(Number)
  const sum = d.slice(0, 12).reduce((acc, v, i) => acc + v * (i % 2 === 0 ? 1 : 3), 0)
  return (10 - (sum % 10)) % 10 === d[12]
}

function isHeicLike(file: File): boolean {
  const type = file.type.toLowerCase()
  if (type === 'image/heic' || type === 'image/heif') return true
  // iOS gallery picks sometimes omit MIME; fall back to extension
  return /\.hei[cf]$/i.test(file.name)
}

/** Downscale into a canvas (never upscale). Safari applies EXIF orientation on the Image. */
function imageToScanCanvas(img: HTMLImageElement, maxEdge: number): HTMLCanvasElement {
  const srcW = img.naturalWidth || img.width
  const srcH = img.naturalHeight || img.height
  if (srcW <= 0 || srcH <= 0) {
    throw new Error('Image invalide (dimensions nulles).')
  }

  const scale = Math.min(1, maxEdge / Math.max(srcW, srcH))
  const dstW  = Math.max(1, Math.round(srcW * scale))
  const dstH  = Math.max(1, Math.round(srcH * scale))

  const canvas = document.createElement('canvas')
  canvas.width  = dstW
  canvas.height = dstH
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Canvas 2D indisponible.')
  ctx.drawImage(img, 0, 0, dstW, dstH)
  return canvas
}

function fileToImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = (ev) => {
      URL.revokeObjectURL(url)
      reject(ev)
    }
    img.src = url
  })
}

function canvasToJpegFile(canvas: HTMLCanvasElement, filename: string, quality = 0.92): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Conversion JPEG impossible.'))
          return
        }
        const base = filename.replace(/\.[^.]+$/, '') || 'photo'
        resolve(new File([blob], `${base}.jpg`, { type: 'image/jpeg' }))
      },
      'image/jpeg',
      quality,
    )
  })
}

async function ensureUploadableImage(file: File): Promise<File> {
  if (!isHeicLike(file)) return file
  const img = await fileToImageElement(file)
  // Cap upload size while preserving enough detail for archives
  const canvas = imageToScanCanvas(img, 2048)
  return canvasToJpegFile(canvas, file.name)
}

export default defineComponent({
  name: 'ScannerView',
  components: { RecallCard },

  setup() {
    const mode        = ref<'barcode' | 'receipt'>('barcode')
    const fileInput   = ref<HTMLInputElement | null>(null)
    const previewUrl  = ref<string | null>(null)
    const imageFile   = ref<File | null>(null)
    const scanning    = ref(false)
    const lookingUp   = ref(false)
    const scanned     = ref(false)
    const results     = ref<GtinResult[]>([])
    const errorMsg    = ref('')
    const ocrProgress = ref(0)
    const saving      = ref(false)
    const saved       = ref(false)
    const saveSnackbar = ref(false)

    function onFileChange(e: Event): void {
      const file = (e.target as HTMLInputElement).files?.[0] ?? null
      if (!file) return
      imageFile.value   = file
      previewUrl.value  = URL.createObjectURL(file)
      results.value     = []
      scanned.value     = false
      errorMsg.value    = ''
      ocrProgress.value = 0
      saved.value       = false
    }

    function reset(): void {
      if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
      previewUrl.value  = null
      imageFile.value   = null
      results.value     = []
      scanned.value     = false
      errorMsg.value    = ''
      ocrProgress.value = 0
      saved.value       = false
      if (fileInput.value) fileInput.value.value = ''
    }

    async function scan(): Promise<void> {
      if (!imageFile.value) return
      scanning.value    = true
      errorMsg.value    = ''
      results.value     = []
      ocrProgress.value = 0

      try {
        let gtins: string[]
        if (mode.value === 'barcode') {
          gtins = await scanBarcode(imageFile.value)
        } else {
          gtins = await scanReceipt(imageFile.value)
        }
        scanned.value  = true
        scanning.value = false
        if (gtins.length > 0) {
          lookingUp.value = true
          results.value   = await lookupGtins(gtins)
          lookingUp.value = false
        }
      } catch (err) {
        errorMsg.value  = err instanceof Error ? err.message : 'Une erreur est survenue.'
        scanning.value  = false
        lookingUp.value = false
      }
    }

    async function lookupGtins(gtins: string[]): Promise<GtinResult[]> {
      const settled = await Promise.allSettled(
        gtins.map(async (gtin): Promise<GtinResult> => {
          const params = new URLSearchParams({ search: gtin, page: '1' })
          const res    = await fetch(`/api/recalls?${params}`, {
            headers: { Accept: 'application/ld+json' },
          })
          if (!res.ok) return { gtin, recalls: [] }
          const data: HydraCollection = await res.json()
          return { gtin, recalls: data.member ?? [] }
        })
      )
      return settled.map((r, i) =>
        r.status === 'fulfilled' ? r.value : { gtin: gtins[i], recalls: [] }
      )
    }

    async function scanBarcode(file: File): Promise<string[]> {
      const img = await fileToImageElement(file)

      // Native BarcodeDetector (Chrome / Edge desktop — absent sur iOS)
      if ('BarcodeDetector' in window) {
        try {
          const detector = new BarcodeDetector({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e'] })
          const detected = await detector.detect(img)
          if (detected.length > 0) {
            return [...new Set(detected.map(r => r.rawValue))]
          }
        } catch {
          // Fall through to ZXing
        }
      }

      // Fallback ZXing: resize + decodeFromCanvas (Safari / iOS / Firefox)
      const { BrowserMultiFormatReader } = await import('@zxing/browser')
      const { BarcodeFormat, DecodeHintType } = await import('@zxing/library')

      const hints = new Map()
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
      ])
      hints.set(DecodeHintType.TRY_HARDER, true)

      const reader = new BrowserMultiFormatReader(hints)

      for (const maxEdge of SCAN_MAX_EDGES) {
        try {
          const canvas = imageToScanCanvas(img, maxEdge)
          const result = reader.decodeFromCanvas(canvas)
          return [result.getText()]
        } catch {
          // Try next scale
        }
      }

      return []
    }

    async function scanReceipt(file: File): Promise<string[]> {
      const worker = await createWorker('eng', undefined, {
        logger: (m: { status: string; progress: number }) => {
          if (m.status === 'recognizing text') {
            ocrProgress.value = Math.round(m.progress * 100)
          }
        },
      })

      try {
        await worker.setParameters({
          tessedit_pageseg_mode: PSM.AUTO,
        })

        const { data } = await worker.recognize(file)
        const matches  = data.text.match(/\b\d{13}\b/g) ?? []
        return [...new Set(matches)].filter(isValidEAN13)
      } finally {
        await worker.terminate()
      }
    }

    async function savePurchase(): Promise<void> {
      if (results.value.length === 0 || !imageFile.value) return
      saving.value = true

      try {
        const uploadFile = await ensureUploadableImage(imageFile.value)
        const fd = new FormData()
        fd.append('image', uploadFile)
        fd.append('type', mode.value)
        const gtins = results.value.map(r => r.gtin)
        gtins.forEach(g => fd.append('gtins[]', g))

        const res = await fetch('/api/purchases', {
          method: 'POST',
          credentials: 'include',
          body: fd,
        })

        if (!res.ok) throw new Error('Erreur lors de l\'enregistrement.')

        saved.value        = true
        saveSnackbar.value = true
      } catch (err) {
        errorMsg.value = err instanceof Error ? err.message : 'Une erreur est survenue.'
      } finally {
        saving.value = false
      }
    }

    return {
      mode,
      fileInput,
      previewUrl,
      scanning,
      lookingUp,
      scanned,
      results,
      errorMsg,
      ocrProgress,
      saving,
      saved,
      saveSnackbar,
      onFileChange,
      reset,
      scan,
      savePurchase,
    }
  },
})
</script>
